/**
 * SEARCH ENGINE - Motor de búsqueda unificado
 * 
 * Servicio de infraestructura compartido para búsqueda híbrida.
 * Combina: Keyword search + Semantic expansion + Vector search (futuro)
 * 
 * USADO POR:
 * - therapist-notes.service.ts
 * - school-practices.service.ts
 * - knowledge-base.service.ts
 * - user-practice-library.service.ts
 */

import { db } from '@/core/config/firebase';
import { Logger } from '@/core/utils';
import { semanticLayerService } from '@/core/semantic';
import { embeddingService } from '../indexing';
import {
    SearchableDocument,
    SearchOptions,
    SearchResult,
    ScoringWeights,
    DEFAULT_SCORING_WEIGHTS,
} from './types';

const logger = Logger.getLogger('SearchEngine');

// ============================================
// SEARCH ENGINE SERVICE
// ============================================

class SearchEngineService {

    /**
     * Búsqueda híbrida unificada
     * 
     * Pipeline:
     * 1. Expandir query con Semantic Layer
     * 2. Buscar por keywords en Firestore
     * 3. (Futuro) Buscar por vector similarity
     * 4. Fusionar y re-rankear resultados
     */
    async search<T extends SearchableDocument>(
        collectionName: string,
        query: string,
        options: SearchOptions = {}
    ): Promise<SearchResult<T>[]> {
        const startTime = Date.now();
        const maxResults = options.maxResults || 20;
        const minScore = options.minScore || 0;

        try {
            // 1. Expandir query con Semantic Layer
            const expansion = this.expandQuery(query, options.useSemanticExpansion !== false);

            // 2. Ejecutar búsqueda por keywords
            const keywordResults = await this.searchByKeywords<T>(
                collectionName,
                expansion.allTerms,
                expansion.conceptIds,
                options.filters
            );

            // 3. (Futuro) Ejecutar búsqueda vectorial
            let vectorResults: SearchResult<T>[] = [];
            if (options.useVectorSearch && await this.hasVectorIndex(collectionName)) {
                vectorResults = await this.searchByVector<T>(collectionName, query, options.filters);
            }

            // 4. Fusionar resultados con RRF
            const merged = this.fuseResults(keywordResults, vectorResults, options.weights);

            // 5. Filtrar y limitar
            const final = merged
                .filter(r => r.score >= minScore)
                .slice(0, maxResults);

            const elapsed = Date.now() - startTime;
            logger.info('Search completed', {
                collection: collectionName,
                query: query.substring(0, 50),
                keywordHits: keywordResults.length,
                vectorHits: vectorResults.length,
                finalResults: final.length,
                elapsed: `${elapsed}ms`
            });

            return final;

        } catch (error: any) {
            logger.error('Search failed', error, { collectionName, query });
            throw error;
        }
    }

    /**
     * Expandir query con Semantic Layer
     */
    private expandQuery(
        query: string,
        useSemantic: boolean
    ): { allTerms: string[]; conceptIds: string[] } {
        // Tokenizar query base
        const baseTerms = query
            .toLowerCase()
            .split(/[\s,;.¿?!¡]+/)
            .filter(t => t.length > 2);

        if (!useSemantic) {
            return { allTerms: baseTerms, conceptIds: [] };
        }

        try {
            const expanded = semanticLayerService.expandQuery(query);

            // Combinar términos base + sinónimos
            const allTerms = [...new Set([
                ...baseTerms,
                ...expanded.synonyms.slice(0, 5),
                ...expanded.searchKeywords.slice(0, 5)
            ])];

            return {
                allTerms,
                conceptIds: expanded.expandedConceptIds
            };
        } catch (error) {
            logger.warn('Semantic expansion failed, using base terms', { error });
            return { allTerms: baseTerms, conceptIds: [] };
        }
    }

    /**
     * Búsqueda por keywords en Firestore
     */
    private async searchByKeywords<T extends SearchableDocument>(
        collectionName: string,
        terms: string[],
        conceptIds: string[],
        filters?: SearchOptions['filters']
    ): Promise<SearchResult<T>[]> {
        // Construir query base
        let query: FirebaseFirestore.Query = db.collection(collectionName);

        // Aplicar filtro de owner
        if (filters?.ownerId) {
            const ownerField = this.getOwnerField(collectionName);
            query = query.where(ownerField, '==', filters.ownerId);
        }

        // Aplicar filtros de metadata
        if (filters?.metadata) {
            for (const [key, value] of Object.entries(filters.metadata)) {
                if (value !== undefined && value !== null) {
                    query = query.where(key, '==', value);
                }
            }
        }

        // Limitar para performance
        const snapshot = await query.limit(200).get();

        // Puntuar cada documento
        const results: SearchResult<T>[] = [];

        for (const doc of snapshot.docs) {
            const data = { id: doc.id, ...doc.data() } as T;
            const { score, matchedTerms } = this.calculateScore(data, terms, conceptIds);

            if (score > 0) {
                results.push({
                    document: data,
                    score,
                    matchedTerms,
                    source: 'keyword'
                });
            }
        }

        // Ordenar por score
        return results.sort((a, b) => b.score - a.score);
    }

    /**
     * Calcular score de un documento
     */
    private calculateScore(
        doc: SearchableDocument,
        terms: string[],
        conceptIds: string[],
        weights: ScoringWeights = DEFAULT_SCORING_WEIGHTS
    ): { score: number; matchedTerms: string[] } {
        let score = 0;
        const matchedTerms: string[] = [];

        // Preparar campos para búsqueda
        const titleLower = (doc.title || '').toLowerCase();
        const contentLower = (doc.content || '').toLowerCase();
        const keywords = doc.keywords || [];
        const tags = doc.tags || [];
        const docConceptIds = doc.conceptIds || [];

        for (const term of terms) {
            const termLower = term.toLowerCase();
            let termMatched = false;

            // Match en título (alto peso)
            if (titleLower.includes(termLower)) {
                score += weights.title;
                termMatched = true;
            }

            // Match en contenido
            if (contentLower.includes(termLower)) {
                // Bonus por múltiples ocurrencias
                const occurrences = (contentLower.match(new RegExp(termLower, 'g')) || []).length;
                score += weights.content * Math.min(occurrences, 3);
                termMatched = true;
            }

            // Match en keywords
            if (keywords.some(k => k.toLowerCase().includes(termLower))) {
                score += weights.keywords;
                termMatched = true;
            }

            // Match en tags
            if (tags.some(t => t.toLowerCase().includes(termLower))) {
                score += weights.tags;
                termMatched = true;
            }

            if (termMatched) {
                matchedTerms.push(term);
            }
        }

        // Bonus por conceptIds del Semantic Layer
        for (const conceptId of conceptIds) {
            if (docConceptIds.includes(conceptId)) {
                score += weights.conceptMatch;
            }
        }

        return { score, matchedTerms: [...new Set(matchedTerms)] };
    }

    /**
     * Búsqueda vectorial con embeddings
     * 
     * Pipeline:
     * 1. Generar embedding del query con Vertex AI
     * 2. Obtener documentos con embeddings de la colección
     * 3. Calcular similitud coseno en cliente
     * 4. Retornar más similares
     * 
     * NOTA: Firestore no soporta nativamente Vector Search en Web SDK.
     * Usamos cálculo de similitud en cliente como alternativa.
     * Para colecciones grandes, considerar Vertex AI Matching Engine.
     */
    private async searchByVector<T extends SearchableDocument>(
        collectionName: string,
        query: string,
        filters?: SearchOptions['filters']
    ): Promise<SearchResult<T>[]> {
        try {
            // 1. Generar embedding del query
            const queryEmbedding = await embeddingService.generateEmbedding(query);

            // 2. Obtener documentos con embeddings
            let docsQuery: any = db.collection(collectionName)
                .where('hasEmbedding', '==', true)
                .limit(100); // Límite para performance

            // Aplicar filtro de owner
            if (filters?.ownerId) {
                const ownerField = this.getOwnerField(collectionName);
                docsQuery = docsQuery.where(ownerField, '==', filters.ownerId);
            }

            const snapshot = await docsQuery.get();

            if (snapshot.empty) {
                logger.debug('No documents with embeddings found', { collectionName });
                return [];
            }

            // 3. Calcular similitud coseno para cada documento
            const results: Array<{ doc: T; similarity: number }> = [];

            for (const docSnapshot of snapshot.docs) {
                const data = { id: docSnapshot.id, ...docSnapshot.data() } as T & { embedding?: number[] };

                if (!data.embedding || data.embedding.length === 0) {
                    continue;
                }

                const similarity = embeddingService.cosineSimilarity(
                    queryEmbedding.embedding,
                    data.embedding
                );

                // Solo incluir si similarity > 0.5 (relevante)
                if (similarity > 0.5) {
                    results.push({ doc: data, similarity });
                }
            }

            // 4. Ordenar por similitud y transformar a SearchResult
            const sorted = results
                .sort((a, b) => b.similarity - a.similarity)
                .slice(0, 20);

            logger.info('Vector search completed', {
                collectionName,
                totalChecked: snapshot.size,
                matches: sorted.length,
            });

            return sorted.map(item => ({
                document: item.doc,
                score: item.similarity * 10, // Normalizar a escala similar a keyword search
                matchedTerms: ['vector'],
                source: 'vector' as const,
            }));

        } catch (error: any) {
            logger.error('Vector search failed', error, { collectionName, query });
            // Fallar silenciosamente - keyword search sigue funcionando
            return [];
        }
    }

    /**
     * Verificar si una colección tiene documentos con embeddings
     */
    private async hasVectorIndex(collectionName: string): Promise<boolean> {
        try {
            // Verificar si hay al menos un documento con embedding
            const snapshot = await db.collection(collectionName)
                .where('hasEmbedding', '==', true)
                .limit(1)
                .get();

            return !snapshot.empty;
        } catch (error) {
            // Si falla, asumir que no hay índice
            logger.warn('Could not check vector index', { collectionName, error });
            return false;
        }
    }


    /**
     * Fusionar resultados con Reciprocal Rank Fusion
     */
    private fuseResults<T extends SearchableDocument>(
        keywordResults: SearchResult<T>[],
        vectorResults: SearchResult<T>[],
        weights?: ScoringWeights
    ): SearchResult<T>[] {
        const K = 60; // Constante RRF estándar
        const keywordWeight = weights?.keywordSearchWeight || 1.0;
        const vectorWeight = weights?.vectorSearchWeight || 1.5;

        const scoreMap = new Map<string, number>();
        const docMap = new Map<string, SearchResult<T>>();

        // Procesar resultados de keywords
        keywordResults.forEach((result, rank) => {
            const rrfScore = keywordWeight / (K + rank);
            const id = result.document.id;
            scoreMap.set(id, (scoreMap.get(id) || 0) + rrfScore);
            docMap.set(id, result);
        });

        // Procesar resultados vectoriales
        vectorResults.forEach((result, rank) => {
            const rrfScore = vectorWeight / (K + rank);
            const id = result.document.id;
            scoreMap.set(id, (scoreMap.get(id) || 0) + rrfScore);
            if (!docMap.has(id)) {
                docMap.set(id, { ...result, source: 'vector' });
            } else {
                // Si está en ambos, marcar como híbrido
                const existing = docMap.get(id)!;
                docMap.set(id, { ...existing, source: 'hybrid' });
            }
        });

        // Construir resultados finales
        return Array.from(docMap.entries())
            .map(([id, result]) => ({
                ...result,
                score: scoreMap.get(id) || 0
            }))
            .sort((a, b) => b.score - a.score);
    }

    /**
     * Obtener campo de owner según colección
     */
    private getOwnerField(collectionName: string): string {
        const ownerFields: Record<string, string> = {
            'therapist_notes': 'therapistId',
            'therapist_note_chunks': 'therapistId',
            'school_practices': 'schoolId',
            'user_practice_library': 'userId',
            'knowledge_base_documents': 'ownerId',
        };
        return ownerFields[collectionName] || 'ownerId';
    }
}

// Singleton export
export const searchEngine = new SearchEngineService();
export default searchEngine;
